import { nil, NO, UIColor, UIRectangle, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurement, RHProcurementPublic } from "../../../../models/prodModels"
import { RHCore } from "../../Core/RHCore"
import { RHFeatureFlags } from "../../Core/RHFeatureFlags"
import { RHFocusManager } from "../../Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHAlertBannerView } from "../../Custom components/RH/RHAlertBannerView"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHNotificationBadge } from "../../Custom components/RH/RHNotificationBadge"
import { RHPanelView } from "../../Custom components/RH/RHPanelView"
import { RHSegmentedTabBarView } from "../../Custom components/RH/RHSegmentedTabBarView"
import { RHButtons, RHColors, RHFont, RHSpacing } from "../../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementDetailsViewTokens, RHQAViewTokens } from "../../Custom components/RH/Utils_slop/RHViewTokens"
import {
    ProcurementQuestionAndAnswerThread,
    RHMergedQAContentSnapshot,
    RHQANotificationSnapshot,
    RHReconciliationRoundSummary
} from "../../SocketClientFunctions"
import { RHPrivateMessagingView } from "../PrivateMessaging/RHPrivateMessagingView"
import { RHQAComposeTarget } from "./RHAskQuestionDialogView"
import { RHCompanyQuestionsCardView } from "./RHCompanyQuestionsCardView"
import { ProcurementRoundContext } from "../ProcurementRoundContext"

export type RHQASubtabKey = "public" | "private"


type RHQAAccessState = {
    isCurrentUserActingAsProcurer: boolean
    isRegisteredTenderer: boolean
    canAnswerMessages: boolean
    shouldReloadVisiblePrivateView?: boolean
}

type RHQAQuestionColumns = {
    unansweredQuestions: ProcurementQuestionAndAnswerThread[]
    answeredQuestions: ProcurementQuestionAndAnswerThread[]
}


// Unanswered specific colors
const UNANSWERED_LIGHT_RED = new UIColor("rgba(232, 111, 111, 0.15)")
const UNANSWERED_RED = new UIColor("rgb(127, 29, 29)")

// Company grouping removed — kept commented for reference.
// interface CompanyQuestionsGroup {
//     companyName: string
//     questions: ProcurementQuestionAndAnswerThread[]
//     hasUnanswered: boolean
// }

class RHQAQuestionsColumnView extends UIView {
    cardViews: RHCompanyQuestionsCardView[] = []
    
    replaceCards(cardViews: RHCompanyQuestionsCardView[]) {
        this.cardViews = cardViews
        this.setNeedsLayout()
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        this.contentBoundsWithInset(0)
            .framesByDistributingViewsAsColumn(
                this.cardViews,
                this.core.paddingLength,
                this.cardViews
            )
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        const width = constrainingWidth || this.contentBounds.width
        const effectiveWidth = width > 0 ? width : 300
        let totalHeight = 0
        
        this.cardViews.forEach(cardView => {
            totalHeight += cardView.intrinsicContentHeight(effectiveWidth)
        })
        
        if (this.cardViews.length > 1) {
            totalHeight += this.core.paddingLength * (this.cardViews.length - 1)
        }
        
        return totalHeight
    }
}

export class RHQAView extends RHPanelView {

    // --- Public/private subtabs ---

    readonly subtabBar = new RHSegmentedTabBarView([
        { key: "public", title: "Avalik" },
        { key: "private", title: "Privaatne" }
    ]).configuredWithObject({
        hidden: YES,
        selectionDidChange: (key) => {
            if (key === "private") {
                this._showPrivateSubtab()
            }
            else {
                this._showPublicSubtab()
            }
        }
    })
    readonly publicSubtabButton = this.subtabBar.tabButtonForKey("public")!
    readonly privateSubtabButton = this.subtabBar.tabButtonForKey("private")!

    readonly privateMessagingView = new RHPrivateMessagingView()
        .addedAsSubviewToView(this)

    _selectedSubtabKey: RHQASubtabKey = "public"
    _privateSubtabUnreadLoadGeneration = 0
    _numberOfPrivateCommunicationMessages = 0
    _questionLoadGeneration = 0
    numberOfCommunicationEntriesDidChange?: (numberOfCommunicationEntries: number) => void

    get selectedSubtabKey(): RHQASubtabKey { return this._selectedSubtabKey }

    _reconciliationRoundChain: RHReconciliationRoundSummary[] = []

    /**
     * Procurement ids the viewer may currently post a new question/broadcast
     * against — the loaded procurement itself when it's a reconciliation
     * round or has no open rounds, or [root, ...open rounds] when it's the
     * root and at least one part has an open round. Populated by
     * loadQuestionsForProcurement; read by ProcurementDetailsViewController
     * when opening RHAskQuestionDialogView / RHProcurerMessageDialogView.
     */
    _composeTargets: RHQAComposeTarget[] = []
    roundContext?: ProcurementRoundContext
    readonly communicationLabelsByProcurementId = new Map<string, string>()
    // --- Unanswered Column ---
    
    readonly unansweredHeaderView = new UIView()
        .addedAsSubviewToView(this)
    
    readonly unansweredHeaderLabel = new UITextView()
        .addedAsSubviewToView(this.unansweredHeaderView)
    
    readonly unansweredCountBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this.unansweredHeaderView)
    
    readonly unansweredQuestionsContainer = new RHQAQuestionsColumnView()
        .addedAsSubviewToView(this)
    
    // --- Answered Column ---
    
    readonly answeredHeaderView = new UIView()
        .addedAsSubviewToView(this)
    
    readonly answeredHeaderLabel = new UITextView()
        .addedAsSubviewToView(this.answeredHeaderView)
    
    readonly answeredCountBanner = new RHAlertBannerView()
        .addedAsSubviewToView(this.answeredHeaderView)
    
    readonly answeredQuestionsContainer = new RHQAQuestionsColumnView()
        .addedAsSubviewToView(this)
    
    // Data
    private procurement?: RHProcurementPublic | RHProcurement
    private _allQuestions: ProcurementQuestionAndAnswerThread[] = []
    _notificationAuditCutoff = ""
    // Company grouping removed — flat per-question lists now drive the two columns.
    // private unansweredCompanyGroups: CompanyQuestionsGroup[] = []
    // private answeredCompanyGroups: CompanyQuestionsGroup[] = []
    private _unansweredQuestions: ProcurementQuestionAndAnswerThread[] = []
    private _answeredQuestions: ProcurementQuestionAndAnswerThread[] = []

    /** Event content from the most recent successful load. Answered IDs are kept
     * separate so a concurrently arriving answer cannot be acknowledged from an
     * older snapshot that rendered only the question body. */
    get loadedNotificationSnapshot(): RHQANotificationSnapshot {
        return this.notificationSnapshotForProcurement()
    }

    notificationSnapshotForProcurement(procurementId?: string): RHQANotificationSnapshot {
        const questions = this._allQuestions.filter(question => !procurementId || question.procurement === procurementId)
        const questionThreads = questions
            .filter(question => question.threadType !== "procurerBroadcast")
        return {
            auditCutoff: this._notificationAuditCutoff,
            questionIds: questionThreads.map(question => question._id.toString()),
            answeredQuestionIds: questionThreads
                .filter(question => !!question.answerText)
                .map(question => question._id.toString()),
            broadcastIds: questions
                .filter(question => question.threadType === "procurerBroadcast")
                .map(question => question._id.toString()),
            amendmentIds: questions.flatMap(question => {
                const automaticMessage = question.automaticMessage
                if (automaticMessage?.kind === "procurementAmendment") {
                    return [automaticMessage.amendmentId]
                }
                return []
            })
        }
    }
    _isCurrentUserActingAsProcurer = NO
    get isCurrentUserActingAsProcurer() { return this._isCurrentUserActingAsProcurer }
    set isCurrentUserActingAsProcurer(isCurrentUserActingAsProcurer: boolean) {
        this.configureAccessState({
            isCurrentUserActingAsProcurer,
            isRegisteredTenderer: this._isRegisteredTenderer,
            canAnswerMessages: this._canAnswerMessages
        })
    }

    _canAnswerMessages = NO
    get canAnswerMessages() { return this._canAnswerMessages }
    set canAnswerMessages(canAnswerMessages: boolean) {
        this.configureAccessState({
            isCurrentUserActingAsProcurer: this._isCurrentUserActingAsProcurer,
            isRegisteredTenderer: this._isRegisteredTenderer,
            canAnswerMessages
        })
    }
    
    _isRegisteredTenderer = NO
    get isRegisteredTenderer() { return this._isRegisteredTenderer }
    set isRegisteredTenderer(isRegisteredTenderer: boolean) {
        this.configureAccessState({
            isCurrentUserActingAsProcurer: this._isCurrentUserActingAsProcurer,
            isRegisteredTenderer,
            canAnswerMessages: this._canAnswerMessages
        })
    }

    configureAccessState(accessState: RHQAAccessState) {
        const actingAsProcurerDidChange =
            this._isCurrentUserActingAsProcurer !== accessState.isCurrentUserActingAsProcurer
        this._isCurrentUserActingAsProcurer = accessState.isCurrentUserActingAsProcurer
        this._isRegisteredTenderer = accessState.isRegisteredTenderer
        this._canAnswerMessages = accessState.canAnswerMessages
        this.unansweredCardViews.everyElement.canAnswerMessages = accessState.canAnswerMessages
        this.answeredCardViews.everyElement.canAnswerMessages = accessState.canAnswerMessages
        this.privateMessagingView.configureAccessState({
            isCurrentUserActingAsProcurer: accessState.isCurrentUserActingAsProcurer,
            canAnswerMessages: accessState.canAnswerMessages,
            shouldReloadVisibleView: accessState.shouldReloadVisiblePrivateView
        })
        this._updateSubtabVisibility()
        if (actingAsProcurerDidChange && this._allQuestions.length > 0) {
            this._splitAndSortQuestions(this._allQuestions)
            this.createQuestionCards()
        }
        this.setNeedsLayout()
    }

    /** Visible public and restricted messages for the "Teabevahetus (N)" tab count. */
    get numberOfCommunicationEntries(): number {
        const numberOfPublicCommunicationMessages = this._allQuestions.reduce(
            (messageCount, question) => {
                let numberOfMessagesInThread = 1
                if (question.answerText) {
                    numberOfMessagesInThread += 1
                }
                return messageCount + numberOfMessagesInThread
            },
            0
        )
        return numberOfPublicCommunicationMessages + this._numberOfPrivateCommunicationMessages
    }

    // Card views
    private unansweredCardViews: RHCompanyQuestionsCardView[] = []
    private answeredCardViews: RHCompanyQuestionsCardView[] = []
    
    bottomView = new UIView().addedAsSubviewToView(this)
    
    override _focusManager = new RHFocusManager(() => {
        const items: (UIView | RHFocusManager)[] = []
        if (!this.subtabBar.hidden) {
            items.push(this.publicSubtabButton, this.privateSubtabButton)
        }
        if (this._selectedSubtabKey === "private") {
            const messaging = this.privateMessagingView
            if (messaging.composeTargetDropdown.superview === messaging) {
                items.push(messaging.composeTargetDropdown)
            }
            if (messaging.threadListView.superview === messaging) {
                items.push(messaging.threadListView.startConversationButton)
                items.push(...messaging.threadListView._rowViews.map(row => row.selectionButton))
            }
            items.push(messaging.threadView.inputArea, messaging.threadView.sendButton)
            if (messaging.threadView.fileUploadView._focusManager) {
                items.push(messaging.threadView.fileUploadView._focusManager)
            }
            return items as UIView[]
        }
        for (const card of this.unansweredCardViews) {
            if (card._focusManager) {
                items.push(card._focusManager)
            }
        }
        for (const card of this.answeredCardViews) {
            if (card._focusManager) {
                items.push(card._focusManager)
            }
        }
        return items as UIView[]
    })
    
    constructor(elementID?: string) {
        super(elementID)

        this._setupUnansweredHeaders()
        this._setupAnsweredHeaders()
        
        this._showPublicSubtab()
    }
    
    
    _setupUnansweredHeaders() {
        this.unansweredHeaderLabel.configureWithObject({
            text: "Hankija vastamata küsimused",
            textColor: RHColors.textPrimary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHProcurementDetailsViewTokens.detailHeaderFontWeight,
                letterSpacing: RHProcurementDetailsViewTokens.detailHeaderLetterSpacing,
                fontFamily: RHFont.familyBase,
                lineHeight: RHFont.lineHeightBase,
                margin: "0",
                userSelect: "none"
            }
        })

        this.unansweredCountBanner.configureBannerWithObject({
            text: "0",
            backgroundColor: RHColors.pinkGlass,
            textColor: RHColors.dangerCoralText,
            borderColor: UIColor.transparentColor,
            fontSize: RHFont.sizeSmall,
            fontWeight: RHQAViewTokens.badgeFontWeight,
            fontFamily: RHFont.familyBase,
            padding: { top: 0, right: RHQAViewTokens.badgePadding, bottom: 0, left: RHQAViewTokens.badgePadding }
        }).configuredWithObject({
            style: {
                borderRadius: RHQAViewTokens.badgeBorderRadius,
                boxShadow: "none",
                whiteSpace: "nowrap"
            }
        })
        this.unansweredCountBanner._textLabel.configureWithObject({
            textAlignment: UITextView.textAlignment.center
        })
    }
    
    _setupAnsweredHeaders() {
        this.answeredHeaderLabel.configureWithObject({
            text: "Hankija vastatud küsimused",
            textColor: RHColors.textPrimary,
            style: {
                fontSize: RHFont.sizeSmall,
                fontWeight: RHProcurementDetailsViewTokens.detailHeaderFontWeight,
                letterSpacing: RHProcurementDetailsViewTokens.detailHeaderLetterSpacing,
                fontFamily: RHFont.familyBase,
                lineHeight: RHFont.lineHeightBase,
                margin: "0",
                userSelect: "none"
            }
        })

        this.answeredCountBanner.configureBannerWithObject({
            text: "0",
            backgroundColor: RHColors.greenGlassMint,
            textColor: RHColors.successGreenBadgeText,
            borderColor: UIColor.transparentColor,
            fontSize: RHFont.sizeSmall,
            fontWeight: RHQAViewTokens.badgeFontWeight,
            fontFamily: RHFont.familyBase,
            padding: { top: 0, right: RHQAViewTokens.badgePadding, bottom: 0, left: RHQAViewTokens.badgePadding }
        }).configuredWithObject({
            style: {
                borderRadius: RHQAViewTokens.badgeBorderRadius,
                boxShadow: "none",
                whiteSpace: "nowrap"
            }
        })
        this.answeredCountBanner._textLabel.configureWithObject({
            textAlignment: UITextView.textAlignment.center
        })
    }

    async loadQuestionsForProcurement(
        procurement: RHProcurementPublic | RHProcurement,
        reconciliationRoundChain?: RHReconciliationRoundSummary[],
        isLoadCurrent: () => boolean = () => YES
    ) {

        const loadGeneration = ++this._questionLoadGeneration
        const previousProcurementId = this.procurement?._id?.toString()
        const procurementId = procurement._id?.toString() ?? ""
        let nextReconciliationRoundChain: RHReconciliationRoundSummary[] = []
        if (reconciliationRoundChain) {
            nextReconciliationRoundChain = reconciliationRoundChain.slice()
        }
        else if (previousProcurementId === procurementId) {
            nextReconciliationRoundChain = this._reconciliationRoundChain.slice()
        }
        try {

            const mergedSnapshot = await RHCore.sharedInstance.qaStore.retrieveMergedQuestions(procurementId)
            if (loadGeneration !== this._questionLoadGeneration || !isLoadCurrent()) {
                return NO
            }

            this.procurement = procurement
            this._reconciliationRoundChain = nextReconciliationRoundChain
            const questions = this.questionsWithDisplayQuestionNumbers(mergedSnapshot.questions)
            this.communicationLabelsByProcurementId.clear()
            this.communicationLabelsByProcurementId.set(
                procurementId,
                ProcurementRoundContext.titleForRound({ roundNumber: 0 })
            )
            for (const source of mergedSnapshot.rounds) {
                let label = "Voor"
                if (procurement.parts.length > 1) {
                    label = "Osa " + source.partNumber + " · voor"
                }
                for (const rounds of this.roundContext?.roundsByPartId.values() ?? []) {
                    const round = rounds.find(candidate => candidate.procurementId === source.procurementId)
                    if (round) {
                        label = ProcurementRoundContext.titleForRound(round)
                        if (procurement.parts.length > 1) {
                            const part = procurement.parts.find(candidate => candidate._id.toString() === round.rootPartId)
                            label = "Osa " + part?.partNumber + " · " + label
                        }
                        break
                    }
                }
                this.communicationLabelsByProcurementId.set(source.procurementId, label)
            }
            this.privateMessagingView.configureCommunicationSources(this.communicationLabelsByProcurementId, this.roundContext)

            // Store raw questions so regrouping is possible when mode changes
            this._allQuestions = questions
            this.numberOfCommunicationEntriesDidChange?.(this.numberOfCommunicationEntries)
            this._notificationAuditCutoff = mergedSnapshot.notificationSnapshotAt
            // Company grouping removed — split into flat chronological lists.
            // this.groupQuestionsByCompany(questions)
            this._splitAndSortQuestions(questions)

            // this.createCompanyCards()
            this.createQuestionCards()
            this.unansweredCountBanner.text = this._unansweredQuestions.length.toString()
            this.answeredCountBanner.text = this._answeredQuestions.length.toString()

            this._composeTargets = this._composeTargetsFromMergedSnapshot(procurementId, mergedSnapshot)

            if (this._selectedSubtabKey === "private") {
                await this.privateMessagingView.loadForProcurement(procurement)
            }
            await this.loadPrivateCommunicationSummary()

            this.setNeedsLayoutUpToRootView()
            return YES
        }
        catch (error) {
            console.error("Error loading questions:", error)
            return NO
        }
    }


    _composeTargetsFromMergedSnapshot(
        procurementId: string,
        mergedSnapshot: RHMergedQAContentSnapshot | undefined
    ): RHQAComposeTarget[] {
        if (!mergedSnapshot) {
            return [{
                procurementId,
                label: ProcurementRoundContext.titleForRound({ roundNumber: 0 })
            }]
        }
        const partNumberByProcurementId = new Map<string, number>(
            mergedSnapshot.rounds.map((round): [string, number] => [round.procurementId, round.partNumber])
        )
        const targetIds = new Set(mergedSnapshot.composeTargetProcurementIds)
        const rounds = [...this.roundContext?.roundsByPartId.values() ?? []].flat()
        const currentRounds = rounds.filter(round => targetIds.has(round.procurementId) &&
            !this.roundContext!.isHistoricalRound(round))
        currentRounds.sort((firstRound, secondRound) => secondRound.roundNumber - firstRound.roundNumber)
        const currentRound = currentRounds.find(round => round.rootPartId === this.roundContext?.focusedPartId) ?? currentRounds[0]
        return [...targetIds].map(targetProcurementId => {
            const isCurrent = targetProcurementId === currentRound?.procurementId
            if (targetProcurementId === procurementId) {
                return {
                    procurementId: targetProcurementId,
                    label: ProcurementRoundContext.titleForRound({ roundNumber: 0 }),
                    isCurrent
                }
            }
            const partNumber = partNumberByProcurementId.get(targetProcurementId)
            let label = "Voor"
            if (partNumber) {
                label = `Osa ${partNumber}`
            }
            const round = rounds.find(candidate => candidate.procurementId === targetProcurementId)
            if (round) {
                label = ProcurementRoundContext.titleForRound(round)
                const parts = this.procurement?.parts ?? []
                if (parts.length > 1) {
                    const part = parts.find(candidate => candidate._id.toString() === round.rootPartId)
                    label = `Osa ${part?.partNumber} · ${label}`
                }
            }
            return { procurementId: targetProcurementId, label, isCurrent }
        })
    }


    /** The tab count and unread dot share one company-scoped snapshot while restricted content stays lazy. */
    async loadPrivateCommunicationSummary() {
        const loadGeneration = ++this._privateSubtabUnreadLoadGeneration
        if (!RHFeatureFlags.procurementPrivateMessagingIsVisible || !this._shouldShowCountBadges || !this.procurement) {
            this._numberOfPrivateCommunicationMessages = 0
            RHNotificationBadge.applyDot(this.privateSubtabButton.titleLabel, NO)
            this.numberOfCommunicationEntriesDidChange?.(this.numberOfCommunicationEntries)
            return
        }
        const procurementId = this.procurement._id + ""
        const activeCompanyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        try {
            const snapshots = await Promise.all([...this.communicationLabelsByProcurementId.keys()].map(sourceId =>
                RHCore.sharedInstance.privateMessageStore.retrieveThreadsForProcurement(sourceId)
            ))
            const threads = snapshots.flatMap(snapshot => snapshot.threads)
            if (
                loadGeneration !== this._privateSubtabUnreadLoadGeneration ||
                this.procurement?._id + "" !== procurementId ||
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== activeCompanyId
            ) {
                return
            }
            this._numberOfPrivateCommunicationMessages = threads.reduce(
                (messageCount, thread) => messageCount + thread.messages.length,
                0
            )
            RHNotificationBadge.applyDot(
                this.privateSubtabButton.titleLabel,
                threads.some(thread => thread.hasUnreadForCurrentViewer),
                "Lugemata privaatne sõnum."
            )
            this.numberOfCommunicationEntriesDidChange?.(this.numberOfCommunicationEntries)
        }
        catch (error) {
            if (
                loadGeneration === this._privateSubtabUnreadLoadGeneration &&
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId === activeCompanyId
            ) {
                console.error("[RHQAView] Failed to load private communication summary:", error)
            }
        }
    }


    invalidatePrivateCommunicationSummary() {
        this._privateSubtabUnreadLoadGeneration += 1
        this._numberOfPrivateCommunicationMessages = 0
        RHNotificationBadge.applyDot(this.privateSubtabButton.titleLabel, NO)
        this.numberOfCommunicationEntriesDidChange?.(this.numberOfCommunicationEntries)
    }
    
    
    override wasAddedToViewTree() {
        super.wasAddedToViewTree()
        RHCore.sharedInstance.qaStore.subscribe(this, (procurementId: string) => {
            if (this.procurement && this.communicationLabelsByProcurementId.has(procurementId)) {
                this.loadQuestionsForProcurement(this.procurement).catch(
                    error => console.error("[RHQAView] Failed to reload questions:", error)
                )
            }
        })
    }
    
    
    override wasRemovedFromViewTree() {
        super.wasRemovedFromViewTree()
        this._privateSubtabUnreadLoadGeneration += 1
        RHCore.sharedInstance.qaStore.unsubscribe(this)
    }
    
    
    private questionsWithDisplayQuestionNumbers(questions: ProcurementQuestionAndAnswerThread[]): ProcurementQuestionAndAnswerThread[] {
        const questionNumberById = new Map<string, number>()
        
        questions.slice()
            .filter(question => question.threadType !== "procurerBroadcast")
            .sort((questionA, questionB) => {
                const createdAtDifference =
                    new Date(questionA.createdAt || 0).getTime() -
                    new Date(questionB.createdAt || 0).getTime()

                if (createdAtDifference != 0) {
                    return createdAtDifference
                }

                return questionA._id.toString().localeCompare(questionB._id.toString())
            }).forEach((question, questionIndex) => {
                questionNumberById.set(question._id.toString(), questionIndex + 1)
            })
        
        return questions.map(question => {
            let questionNumber = question.questionNumber
            
            if (typeof questionNumber != "number" || !Number.isFinite(questionNumber)) {
                questionNumber = questionNumberById.get(question._id.toString()) || 0
            }
            
            return {
                ...question,
                questionNumber
            }
        })
    }
    
    
    // Splits questions into flat unanswered / answered lists, each chronologically
    // sorted newest-first: unanswered by question time, answered by answer time.
    _questionColumnsForQuestions(
        questions: ProcurementQuestionAndAnswerThread[]
    ): RHQAQuestionColumns {
        const unansweredQuestions = questions
            .filter(question => question.threadType !== "procurerBroadcast" && !question.answerText)
            .sort((questionA, questionB) =>
                new Date(questionB.createdAt).getTime() - new Date(questionA.createdAt).getTime()
            )
        const answeredQuestions = questions
            .filter(question => question.threadType === "procurerBroadcast" || !!question.answerText)
            .sort((questionA, questionB) =>
                this._sortTimestampForAnsweredColumn(questionB) - this._sortTimestampForAnsweredColumn(questionA)
            )
        return { unansweredQuestions, answeredQuestions }
    }


    _splitAndSortQuestions(questions: ProcurementQuestionAndAnswerThread[]) {
        const questionColumns = this._questionColumnsForQuestions(questions)
        this._unansweredQuestions = questionColumns.unansweredQuestions
        this._answeredQuestions = questionColumns.answeredQuestions
    }


    private _sortTimestampForAnsweredColumn(question: ProcurementQuestionAndAnswerThread): number {
        return new Date(question.answeredAt || question.updatedAt || question.createdAt).getTime()
    }
    
    
    // One card per question. Two columns kept: unanswered questions on the left,
    // answered question+answer pairs on the right — both already chronologically
    // sorted by _splitAndSortQuestions (newest on top).
    private createQuestionCards() {
        this.unansweredCardViews.forEach(card => card.removeFromSuperview())
        this.answeredCardViews.forEach(card => card.removeFromSuperview())

        this.unansweredCardViews = []
        this.answeredCardViews = []

        this._unansweredQuestions.forEach(question => {
            const card = new RHCompanyQuestionsCardView(
                question.authorCompanyName ?? "Anonüümne",
                [question],
                this._procurementForQuestion(question)
            ).addedAsSubviewToView(this.unansweredQuestionsContainer)

            const source = this.roundContext?.informationByProcurementId.get(question.procurement)
            card.configureWithObject({
                isLocked: !source && question.procurement !== this.procurement?._id?.toString(),
                isCurrentUserActingAsProcurer: this.isCurrentUserActingAsProcurer,
                canAnswerMessages: source?.capabilities?.canAnswerMessages ??
                    (question.procurement === this.procurement?._id?.toString() && this.canAnswerMessages)
            })
            if (this.communicationLabelsByProcurementId.size > 1) {
                card.contextBadge.text = this.communicationLabelsByProcurementId.get(question.procurement) ?? ""
                card.contextBadge.addedAsSubviewToView(card)
            }

            card.answerDidSubmit = (questionId, answer) => {
                if (this.procurement) {
                    RHCore.sharedInstance.qaStore.invalidate(this.procurement._id + "")
                    this.loadQuestionsForProcurement(this.procurement).catch(
                        error => console.error("[RHQAView] Failed to reload after answer:", error)
                    )
                }
            }
            this.unansweredCardViews.push(card)
        })

        this._answeredQuestions.forEach(question => {
            const card = new RHCompanyQuestionsCardView(
                question.authorCompanyName ?? "Anonüümne",
                [question],
                this._procurementForQuestion(question)
            ).addedAsSubviewToView(this.answeredQuestionsContainer)

            const source = this.roundContext?.informationByProcurementId.get(question.procurement)
            card.configureWithObject({
                isLocked: !source && question.procurement !== this.procurement?._id?.toString(),
                isCurrentUserActingAsProcurer: this.isCurrentUserActingAsProcurer,
                canAnswerMessages: source?.capabilities?.canAnswerMessages ??
                    (question.procurement === this.procurement?._id?.toString() && this.canAnswerMessages)
            })
            if (this.communicationLabelsByProcurementId.size > 1) {
                card.contextBadge.text = this.communicationLabelsByProcurementId.get(question.procurement) ?? ""
                card.contextBadge.addedAsSubviewToView(card)
            }

            card.answerDidSubmit = (questionId, answer) => {
                if (this.procurement) {
                    RHCore.sharedInstance.qaStore.invalidate(this.procurement._id + "")
                    this.loadQuestionsForProcurement(this.procurement).catch(
                        error => console.error("[RHQAView] Failed to reload after answer:", error)
                    )
                }
            }

            this.answeredCardViews.push(card)
        })

        this.unansweredQuestionsContainer.replaceCards(this.unansweredCardViews)
        this.answeredQuestionsContainer.replaceCards(this.answeredCardViews)
    }

    _procurementForQuestion(question: ProcurementQuestionAndAnswerThread): RHProcurement | RHProcurementPublic {
        const source = this.roundContext?.informationByProcurementId.get(question.procurement)?.procurement
        if (source && this.procurement) {
            return { ...source, title: this.procurement.title, referenceNumber: this.procurement.referenceNumber }
        }
        return this.procurement!
    }
    
    highlightQuestion(questionId: string) {
        const allCardViews = [...this.unansweredCardViews, ...this.answeredCardViews]
        for (const card of allCardViews) {
            for (const pairView of card.questionViews) {
                if (pairView.questionId === questionId) {
                    pairView.highlight()
                    return
                }
            }
        }
    }


    showPrivateConversation(offererCompanyId?: string) {
        if (this.subtabBar.hidden) {
            return
        }
        // Binding the private view to a new procurement clears its previous selection.
        this._showPrivateSubtab()
        if (offererCompanyId) {
            this.privateMessagingView.selectOffererCompany(offererCompanyId)
        }
    }
    
    
    _headerBadgeWidth(badge: RHAlertBannerView): number {
        return Math.max(
            28,
            badge._textLabel.intrinsicContentWidth(RHQAViewTokens.badgeHeight) +
            12
        )
    }
    
    private get _shouldShowCountBadges(): boolean {
        return this._isCurrentUserActingAsProcurer || this._isRegisteredTenderer
    }

    /** Only the procurement owner or a registered tenderer can ever have a private thread — everyone else never sees the subtab bar at all. */
    _updateSubtabVisibility() {
        const shouldShowSubtabs = RHFeatureFlags.procurementPrivateMessagingIsVisible && this._shouldShowCountBadges
        this.subtabBar.hidden = !shouldShowSubtabs
        if (shouldShowSubtabs) {
            this.subtabBar.addedAsSubviewToView(this)
        }
        else {
            this.subtabBar.removeFromSuperview()
        }
        if (!shouldShowSubtabs && this._selectedSubtabKey !== "public") {
            this._showPublicSubtab()
            return
        }
        this.setNeedsLayout()
    }

    _showPublicSubtab() {
        this._selectedSubtabKey = "public"
        this.subtabBar.selectedKey = "public"

        const publicContentViews = [
            this.unansweredHeaderView,
            this.unansweredQuestionsContainer,
            this.answeredHeaderView,
            this.answeredQuestionsContainer
        ]
        for (const view of publicContentViews) {
            if (view.superview !== this) {
                view.addedAsSubviewToView(this)
            }
        }

        this.privateMessagingView.hidden = YES
        this.privateMessagingView.removeFromSuperview()
        this.unansweredHeaderView.hidden = NO
        this.unansweredQuestionsContainer.hidden = NO
        this.answeredHeaderView.hidden = NO
        this.answeredQuestionsContainer.hidden = NO
        this.bottomView.hidden = NO

        this.setNeedsLayoutUpToRootView()
    }

    _showPrivateSubtab() {
        this._selectedSubtabKey = "private"
        this.subtabBar.selectedKey = "private"

        const publicContentViews = [
            this.unansweredHeaderView,
            this.unansweredQuestionsContainer,
            this.answeredHeaderView,
            this.answeredQuestionsContainer
        ]
        for (const view of publicContentViews) {
            view.removeFromSuperview()
        }

        this.privateMessagingView.hidden = NO
        if (this.privateMessagingView.superview !== this) {
            this.privateMessagingView.addedAsSubviewToView(this)
        }
        this.unansweredHeaderView.hidden = YES
        this.unansweredQuestionsContainer.hidden = YES
        this.answeredHeaderView.hidden = YES
        this.answeredQuestionsContainer.hidden = YES
        this.bottomView.hidden = YES

        if (this.procurement) {
            void this.privateMessagingView.loadForProcurement(this.procurement)
        }

        this.setNeedsLayoutUpToRootView()
    }

    _layoutHeaderContent(headerView: UIView, headerLabel: UITextView, countBanner: RHAlertBannerView) {
        if (this._shouldShowCountBadges) {
            if (countBanner.superview !== headerView) {
                countBanner.addedAsSubviewToView(headerView)
            }
            const badgeWidth = this._headerBadgeWidth(countBanner)
            const maxLabelWidth = Math.max(
                0,
                headerView.bounds.width - badgeWidth - RHSpacing.tiny
            )
            const labelWidth = Math.min(
                headerLabel.intrinsicContentWidth(RHQAViewTokens.headerRowHeight) +
                RHSpacing.medium,
                maxLabelWidth
            )
            const groupWidth = labelWidth + RHSpacing.tiny + badgeWidth
            headerView.bounds
                .rectangleWithWidth(groupWidth, 0.5)
                .distributeViewsAlongWidth(
                    [headerLabel, countBanner],
                    nil,
                    RHSpacing.tiny,
                    [labelWidth, badgeWidth]
                )
            countBanner.frame = countBanner.frame
                .rectangleWithHeight(RHQAViewTokens.badgeHeight)
                .rectangleByCenteringInRectangle(countBanner.frame)
        }
        else {
            if (countBanner.superview === headerView) {
                countBanner.removeFromSuperview()
            }
            const labelWidth = Math.min(
                headerLabel.intrinsicContentWidth(RHQAViewTokens.headerRowHeight) +
                RHSpacing.medium,
                headerView.bounds.width
            )
            headerLabel.frame = headerView.bounds
                .rectangleWithWidth(labelWidth, 0.5)
                .rectangleWithHeight(RHQAViewTokens.headerRowHeight)
        }
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInsets(padding, padding, 0, padding)
        const headerHeight = RHQAViewTokens.headerRowHeight
        const isPublic = this._selectedSubtabKey === "public"
        bounds.rectangleWithHeight(0)
            .IF(!this.subtabBar.hidden)
            .rectangleForNextRow(0, this.subtabBar)
            .assignedAsFrameOfView(this.subtabBar)
            .ENDIF()
            .rectangleForNextRow(padding, 0)
            .IF(!isPublic)
            .rectangleWithHeight(this.privateMessagingView)
            .assignedAsFrameOfView(this.privateMessagingView)
            .ELSE_IF(bounds.width < 640)
            .rectangleWithHeight(headerHeight)
            .assignedAsFrameOfView(this.unansweredHeaderView)
            .rectangleForNextRow(padding, this.unansweredQuestionsContainer)
            .assignedAsFrameOfView(this.unansweredQuestionsContainer)
            .rectangleForNextRow(padding, headerHeight)
            .assignedAsFrameOfView(this.answeredHeaderView)
            .rectangleForNextRow(padding, this.answeredQuestionsContainer)
            .assignedAsFrameOfView(this.answeredQuestionsContainer)
            .ELSE()
            .TRANSFORM(origin => {
                const [unansweredBounds, answeredBounds] = origin.rectanglesBySplittingWidth([1, 1], padding)
                unansweredBounds.rectangleWithHeight(headerHeight)
                    .assignedAsFrameOfView(this.unansweredHeaderView)
                    .rectangleForNextRow(padding, this.unansweredQuestionsContainer)
                    .assignedAsFrameOfView(this.unansweredQuestionsContainer)
                answeredBounds.rectangleWithHeight(headerHeight)
                    .assignedAsFrameOfView(this.answeredHeaderView)
                    .rectangleForNextRow(padding, this.answeredQuestionsContainer)
                    .assignedAsFrameOfView(this.answeredQuestionsContainer)
                return UIRectangle.boundingBoxForPoints([
                    this.unansweredQuestionsContainer.frame.max,
                    this.answeredQuestionsContainer.frame.max
                ])
            })
            .ENDIF()
            .rectangleForNextRow(0, padding)
            .assignedAsFrameOfView(this.bottomView)
        if (isPublic) {
            this._layoutHeaderContent(this.unansweredHeaderView, this.unansweredHeaderLabel, this.unansweredCountBanner)
            this._layoutHeaderContent(this.answeredHeaderView, this.answeredHeaderLabel, this.answeredCountBanner)
        }
    }
}
